import React, {
  useState,
  forwardRef,
  useImperativeHandle,
  useEffect,
} from "react";
import {
  Box,
  Divider,
  Alert,
  CircularProgress,
  Typography,
  Button,
} from "@mui/material";
import {
  Payment as PaymentIcon,
  CheckCircle as CheckCircleIcon,
  Info as InfoIcon,
  Error as ErrorIcon,
  CompareArrows as CompareArrowsIcon,
  Block as BlockIcon,
} from "@mui/icons-material";
import CardExpandible from "../../resumenSucursal/components/CardExpandible";
import { useConciliacion } from "./hooks/useConciliacion";
import { useNoAprobadosMP } from "./hooks/useNoAprobadosMP";
import TablaNoAprobados, { exportNoAprobadosToCSV } from "./components/TablaNoAprobados";
import { getEmpresaNombre } from "./utils/helpers";
import FiltrosBar from "./components/FiltrosBar";
import TabCards from "./components/TabCards";
import DiferenciaTotal from "./components/DiferenciaTotal";
import TablaConciliacion from "./components/TablaConciliacion";
import DialogConfirmacion from "./components/DialogConfirmacion";
import DialogConfirmacionDesconciliacion from "./components/DialogConfirmacionDesconciliacion";
import { conciliarPagos, quitaConciliacion } from "./services/pagos.service";

const ResumenGeneral = forwardRef(
  ({ desde, hasta, empresa, loading = false, triggerRecarga = 0 }, ref) => {
    // Estado de conciliación
    const {
      conciliacion,
      resumen,
      metadata,
      observaciones,
      cargando,
      error,
      recargar,
      recargarObservaciones,
    } = useConciliacion(empresa, desde, hasta, triggerRecarga);

    // Pagos MP no aprobados (independiente de la conciliación)
    const {
      noAprobados,
      cargandoNoAprobados,
      errorNoAprobados,
    } = useNoAprobadosMP(empresa, desde, hasta, triggerRecarga);

    // Estados de UI
    const [selectedTab, setSelectedTab] = useState(0);
    const [expandedRows, setExpandedRows] = useState({});
    const [searchTerm, setSearchTerm] = useState("");
    const [filtroMontoMin, setFiltroMontoMin] = useState("");
    const [filtroMontoMax, setFiltroMontoMax] = useState("");
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(5);
    const [ordenColumna, setOrdenColumna] = useState("fecha");
    const [ordenDireccion, setOrdenDireccion] = useState("desc");

    // Estados para selección de pagos
    const [selectedItems, setSelectedItems] = useState({});
    const [showConfirmDialog, setShowConfirmDialog] = useState(false);
    const [updating, setUpdating] = useState(false);

    // Estados para desconciliación individual
    const [desconciliarItem, setDesconciliarItem] = useState(null);
    const [desconciliando, setDesconciliando] = useState(false);

    // Exponer métodos al padre
    useImperativeHandle(ref, () => ({
      getConciliacion: () => conciliacion,
      getResumen: () => resumen,
      getMetadata: () => metadata,
      getCargando: () => cargando,
      recargar: () => recargar(),
    }));

    // Resetear página cuando cambian los filtros o el ordenamiento
    useEffect(() => {
      setPage(0);
    }, [
      selectedTab,
      searchTerm,
      filtroMontoMin,
      filtroMontoMax,
      ordenColumna,
      ordenDireccion,
    ]);

    // Helpers
    const getDatosTab = () => {
      if (!conciliacion) return [];
      switch (selectedTab) {
        case 0:
          return conciliacion.coincidencias || [];
        case 1:
          return conciliacion.soloDB || [];
        case 2:
          return conciliacion.soloMP || [];
        case 4:
          return noAprobados || [];
        default:
          return [];
      }
    };

    const filtrarDatos = (datos) => {
      if (!datos) return [];
      return datos.filter((item) => {
        const dbData = item.db || item;
        const mpData = item.mp || item;

        if (searchTerm) {
          const searchLower = searchTerm.toLowerCase();
          const referencia = (
            mpData?.external_reference ||
            dbData?.detalle ||
            ""
          ).toLowerCase();
          const cliente = (dbData?.cliente || "").toLowerCase();
          const pedido = (dbData?.pedido || "").toString().toLowerCase();
          if (
            !referencia.includes(searchLower) &&
            !cliente.includes(searchLower) &&
            !pedido.includes(searchLower)
          ) {
            return false;
          }
        }

        if (filtroMontoMin) {
          const montoDB = parseFloat(dbData?.monto) || 0;
          const montoMP = parseFloat(mpData?.transaction_amount) || 0;
          const monto =
            selectedTab === 1
              ? montoDB
              : selectedTab === 2
                ? montoMP
                : Math.max(montoDB, montoMP);
          if (monto < parseFloat(filtroMontoMin)) return false;
        }

        if (filtroMontoMax) {
          const montoDB = parseFloat(dbData?.monto) || 0;
          const montoMP = parseFloat(mpData?.transaction_amount) || 0;
          const monto =
            selectedTab === 1
              ? montoDB
              : selectedTab === 2
                ? montoMP
                : Math.max(montoDB, montoMP);
          if (monto > parseFloat(filtroMontoMax)) return false;
        }

        return true;
      });
    };

    const ordenarDatos = (datos, columna, direccion) => {
      if (!datos) return [];
      return [...datos].sort((a, b) => {
        const dbA = a.db || a;
        const dbB = b.db || b;
        const mpA = a.mp || a;
        const mpB = b.mp || b;
        let valorA, valorB;
        switch (columna) {
          case "referencia":
            valorA = (
              mpA?.external_reference ||
              dbA?.detalle ||
              ""
            ).toLowerCase();
            valorB = (
              mpB?.external_reference ||
              dbB?.detalle ||
              ""
            ).toLowerCase();
            break;
          case "fecha":
            valorA = dbA?.fecha || mpA?.date_approved || "";
            valorB = dbB?.fecha || mpB?.date_approved || "";
            break;
          case "montoDB":
            valorA = parseFloat(dbA?.monto) || 0;
            valorB = parseFloat(dbB?.monto) || 0;
            break;
          case "montoMP":
            valorA = parseFloat(mpA?.transaction_amount) || 0;
            valorB = parseFloat(mpB?.transaction_amount) || 0;
            break;
          case "montoNeto":
            valorA = parseFloat(mpA?.recibido) || 0;
            valorB = parseFloat(mpB?.recibido) || 0;
            break;
          default:
            return 0;
        }
        if (valorA < valorB) return direccion === "asc" ? -1 : 1;
        if (valorA > valorB) return direccion === "asc" ? 1 : -1;
        return 0;
      });
    };

    const obtenerDatosFiltradosYOrdenados = () => {
      const datos = getDatosTab();
      const datosFiltrados = filtrarDatos(datos);
      return ordenarDatos(datosFiltrados, ordenColumna, ordenDireccion);
    };

    const datosActuales = obtenerDatosFiltradosYOrdenados();
    const emptyRows =
      page > 0
        ? Math.max(0, (1 + page) * rowsPerPage - datosActuales.length)
        : 0;
    const datosPaginados = datosActuales.slice(
      page * rowsPerPage,
      page * rowsPerPage + rowsPerPage,
    );

    // Handlers
    const limpiarFiltros = () => {
      setSearchTerm("");
      setFiltroMontoMin("");
      setFiltroMontoMax("");
    };

    const handleOrdenar = (columna) => {
      if (ordenColumna === columna) {
        setOrdenDireccion(ordenDireccion === "asc" ? "desc" : "asc");
      } else {
        setOrdenColumna(columna);
        setOrdenDireccion("asc");
      }
    };

    const handleTabChange = (tabIndex) => {
      setSelectedTab(tabIndex);
      setPage(0);
      setExpandedRows({});
    };

    const toggleExpandRow = (id) => {
      setExpandedRows((prev) => ({ ...prev, [id]: !prev[id] }));
    };

    const handleChangePage = (event, newPage) => {
      setPage(newPage);
      setExpandedRows({});
    };

    const handleChangeRowsPerPage = (event) => {
      setRowsPerPage(parseInt(event.target.value, 10));
      setPage(0);
      setExpandedRows({});
    };

    const handleExportCSV = () => {
  if (selectedTab === 4) {
    exportNoAprobadosToCSV(
      datosActuales,
      `conciliacion_no_aprobados_${getEmpresaNombre(empresa)}_${new Date().toISOString().split("T")[0]}.csv`,
    );
    return;
  }

  // Obtener el nombre del tab según selectedTab
  const tabNames = ['coincidencias', 'sistema', 'mp', 'diferencia'];
  const tabName = tabNames[selectedTab] || 'conciliacion';
  
  const filename = `conciliacion_${tabName}_${getEmpresaNombre(empresa)}_${new Date().toISOString().split("T")[0]}.csv`;
  
  import("./utils/helpers").then(({ exportToCSV }) => {
    exportToCSV(datosActuales, filename);
  });
};

    // Estilos de tabs
    const getTabIcon = (tabIndex) => {
      switch (tabIndex) {
        case 0:
          return <CheckCircleIcon sx={{ fontSize: 20, color: "#2e7d32" }} />;
        case 1:
          return <InfoIcon sx={{ fontSize: 20, color: "#ed6c02" }} />;
        case 2:
          return <ErrorIcon sx={{ fontSize: 20, color: "#d32f2f" }} />;
        case 3:
          return <CompareArrowsIcon sx={{ fontSize: 20, color: "#1976d2" }} />;
        case 4:
          return <BlockIcon sx={{ fontSize: 20, color: "#6a1b9a" }} />;
        default:
          return null;
      }
    };

    const getTabBgColor = (tabIndex) => {
      switch (tabIndex) {
        case 0:
          return "#e8f5e9";
        case 1:
          return "#fff3e0";
        case 2:
          return "#fce4ec";
        case 3:
          return "#e3f2fd";
        case 4:
          return "#f3e5f5";
        default:
          return "#fafafa";
      }
    };

    const getTabBorderColor = (tabIndex) => {
      switch (tabIndex) {
        case 0:
          return "#4caf50";
        case 1:
          return "#ff9800";
        case 2:
          return "#f44336";
        case 3:
          return "#1976d2";
        case 4:
          return "#6a1b9a";
        default:
          return "#e0e0e0";
      }
    };

    const getTabTextColor = (tabIndex) => {
      switch (tabIndex) {
        case 0:
          return "#2e7d32";
        case 1:
          return "#ed6c02";
        case 2:
          return "#d32f2f";
        case 3:
          return "#1976d2";
        case 4:
          return "#6a1b9a";
        default:
          return "#424242";
      }
    };

    // Handlers para selección de pagos
    const handleSelectItem = (key, checked) => {
      setSelectedItems((prev) => ({
        ...prev,
        [key]: checked,
      }));
    };

    const handleSelectAll = (checked) => {
      const newSelected = {};
      datosPaginados.forEach((item, index) => {
        const globalIndex = page * rowsPerPage + index;
        const key = `${selectedTab}-${globalIndex}`;
        const dbData = item.db || item;
        // Solo seleccionar si no está conciliado
        if (dbData?.conciliado !== 1) {
          newSelected[key] = checked;
        }
      });
      setSelectedItems(newSelected);
    };

    const handleMarcarConciliacion = () => {
      const selectedKeys = Object.keys(selectedItems).filter(
        (key) => selectedItems[key],
      );
      if (selectedKeys.length === 0) {
        alert("Selecciona al menos un pago");
        return;
      }
      setShowConfirmDialog(true);
    };

  const handleConfirmConciliacion = async () => {
  setUpdating(true);
  try {
    const selectedKeys = Object.keys(selectedItems).filter(
      (key) => selectedItems[key],
    );
    const idsToUpdate = selectedKeys
      .map((key) => {
        const index = parseInt(key.split("-")[1]);
        const item = datosActuales[index];
        const dbData = item.db || item;
        return dbData.id;
      })
      .filter((id) => id);

    if (idsToUpdate.length === 0) {
      alert('No hay pagos válidos para conciliar');
      return;
    }

    await conciliarPagos(idsToUpdate);
    
    setSelectedItems({});
    setShowConfirmDialog(false);
    recargar(); // Recargar datos
  } catch (error) {
    console.error("Error:", error);
    alert(error.message || "Error al conciliar los pagos");
  } finally {
    setUpdating(false);
  }
};

    // Handlers para desconciliación individual
    const handleAbrirDesconciliar = (dbData) => {
      setDesconciliarItem(dbData);
    };

    const handleCerrarDesconciliar = () => {
      if (desconciliando) return;
      setDesconciliarItem(null);
    };

    const handleConfirmDesconciliar = async () => {
      if (!desconciliarItem?.id) return;
      setDesconciliando(true);
      try {
        await quitaConciliacion([desconciliarItem.id]);
        setDesconciliarItem(null);
        recargar();
      } catch (error) {
        console.error("Error:", error);
        alert(error.message || "Error al desconciliar el pago");
      } finally {
        setDesconciliando(false);
      }
    };

    // Estados de carga
    if (loading || cargando) {
      return (
        <CardExpandible
          title={`Conciliación - ${getEmpresaNombre(empresa)}`}
          icon={PaymentIcon}
        >
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              height: 300,
            }}
          >
            <CircularProgress size={40} sx={{ color: "#757575" }} />
          </Box>
        </CardExpandible>
      );
    }

    if (!desde || !hasta || !empresa) {
      let mensaje = "";
      if (!empresa) mensaje = "Selecciona una empresa";
      else if (!desde || !hasta) mensaje = "Selecciona un rango de fechas";
      return (
        <CardExpandible
          title={`Conciliación - ${getEmpresaNombre(empresa)}`}
          icon={PaymentIcon}
        >
          <Box sx={{ p: 3, textAlign: "center" }}>
            <Typography variant="body2" color="textSecondary">
              {mensaje}
            </Typography>
          </Box>
        </CardExpandible>
      );
    }

    if (error) {
      return (
        <CardExpandible
          title={`Conciliación - ${getEmpresaNombre(empresa)}`}
          icon={PaymentIcon}
        >
          <Alert severity="error" sx={{ m: 2 }}>
            {error}
          </Alert>
        </CardExpandible>
      );
    }

    if (!conciliacion) {
      return (
        <CardExpandible
          title={`Conciliación - ${getEmpresaNombre(empresa)}`}
          icon={PaymentIcon}
        >
          <Box sx={{ p: 3, textAlign: "center" }}>
            <Typography variant="body2" color="textSecondary">
              No hay datos de conciliación disponibles
            </Typography>
          </Box>
        </CardExpandible>
      );
    }

    // Datos para tabs
    const tabData = [
      {
        label: "Coincidencias",
        count: conciliacion.coincidencias?.length || 0,
        icon: getTabIcon(0),
      },
      {
        label: "Sistema",
        count: conciliacion.soloDB?.length || 0,
        icon: getTabIcon(1),
      },
      {
        label: "Mercado Pago",
        count: conciliacion.soloMP?.length || 0,
        icon: getTabIcon(2),
      },
      {
        label: "Diferencia",
        icon: getTabIcon(3),
      },
      {
        label: "No aprobados MP",
        count: cargandoNoAprobados
          ? "…"
          : errorNoAprobados
            ? "!"
            : noAprobados?.length || 0,
        icon: getTabIcon(4),
      },
    ];

    return (
      <CardExpandible
        title={`Conciliación - ${getEmpresaNombre(empresa)}`}
        icon={PaymentIcon}
      >
        <Box>
          {/* Filtros */}
          <FiltrosBar
            searchTerm={searchTerm}
            setSearchTerm={setSearchTerm}
            filtroMontoMin={filtroMontoMin}
            setFiltroMontoMin={setFiltroMontoMin}
            filtroMontoMax={filtroMontoMax}
            setFiltroMontoMax={setFiltroMontoMax}
            limpiarFiltros={limpiarFiltros}
            handleExportCSV={handleExportCSV}
            totalResultados={datosActuales.length}
            totalFiltrados={getDatosTab().length}
            loading={cargando}
          />

          <Divider sx={{ mb: 2 }} />

          {(selectedTab === 0 || selectedTab === 1) && (
            <Box sx={{ mb: 2, display: "flex", justifyContent: "flex-end" }}>
              <Button
                variant="contained"
                color="success"
                onClick={handleMarcarConciliacion}
                disabled={!Object.values(selectedItems).some((v) => v)}
                startIcon={<CheckCircleIcon />}
              >
                Marcar Conciliación (
                {Object.values(selectedItems).filter((v) => v).length})
              </Button>
            </Box>
          )}
          {/* Tabs Cards */}
          <TabCards
            tabData={tabData}
            selectedTab={selectedTab}
            onTabChange={handleTabChange}
            getTabBgColor={getTabBgColor}
            getTabBorderColor={getTabBorderColor}
            getTabTextColor={getTabTextColor}
          />

          {/* Contenido */}
          {selectedTab === 3 ? (
            <DiferenciaTotal resumen={resumen} />
          ) : selectedTab === 4 ? (
            <TablaNoAprobados
              datos={datosActuales}
              conciliacion={conciliacion}
              cargando={cargandoNoAprobados}
              error={errorNoAprobados}
            />
          ) : (
            <>
              <TablaConciliacion
                datosPaginados={datosPaginados}
                datosActuales={datosActuales}
                page={page}
                rowsPerPage={rowsPerPage}
                selectedTab={selectedTab}
                emptyRows={emptyRows}
                expandedRows={expandedRows}
                toggleExpandRow={toggleExpandRow}
                handleChangePage={handleChangePage}
                handleChangeRowsPerPage={handleChangeRowsPerPage}
                ordenColumna={ordenColumna}
                ordenDireccion={ordenDireccion}
                onOrdenar={handleOrdenar}
                selectedItems={selectedItems}
                onSelectItem={handleSelectItem}
                onSelectAll={handleSelectAll}
                showCheckbox={selectedTab === 0 || selectedTab === 1}
                empresa={empresa}
                onDesconciliar={handleAbrirDesconciliar}
                observaciones={observaciones}
                onObservacionGuardada={recargarObservaciones}
              />

              {metadata && (
                <Box
                  sx={{
                    mt: 2,
                    display: "flex",
                    justifyContent: "space-between",
                  }}
                >
                  <Typography variant="caption" color="textSecondary">
                    Empresa: {getEmpresaNombre(empresa)} (ID: {empresa})
                  </Typography>
                  <Typography variant="caption" color="textSecondary">
                    Procesado:{" "}
                    {new Date(metadata.fechaProcesamiento).toLocaleString()}
                  </Typography>
                </Box>
              )}
            </>
          )}
        </Box>
        <DialogConfirmacion
          open={showConfirmDialog}
          onClose={() => setShowConfirmDialog(false)}
          onConfirm={handleConfirmConciliacion}
          selectedItems={selectedItems}
          datosActuales={datosActuales}
          updating={updating}
        />
        <DialogConfirmacionDesconciliacion
          open={!!desconciliarItem}
          onClose={handleCerrarDesconciliar}
          onConfirm={handleConfirmDesconciliar}
          item={desconciliarItem}
          updating={desconciliando}
        />
      </CardExpandible>
    );
  },
);

ResumenGeneral.displayName = "ResumenGeneral";

export default ResumenGeneral;
